<template>
<div class="px-4">
    <div class="row">
        <div class="col" v-if="!ready">
            <sau-loading>
                <template slot="message">Fetching Invoice Details</template>
            </sau-loading>
        </div>
        <div class="col" v-if="ready">
            <div>
                <!-- View Invoice -->
                <div class="text-center" :class="[invoice.status === 'Paid' ? '' : 'pb-3']">
                    <h4 class="text-center sau-pm-reset">Invoice</h4>
                    <img :src="getStatusImage" alt="Invoice"/>
                    <p class="sau-pm-reset pb-2">Invoice Number: {{ invoice.id }}</p>
                    <div v-if="invoice.status == 'Unpaid' || invoice.status == 'Overdue'">
                        <a v-if="paymentView" href="#" @click.prevent="changePaymentView(false)" class="sau-link-button sm d-block py-2">View Invoice</a>
                        <a v-else-if="paymentMethodSelected" href="#" @click.prevent="changePaymentView(true)" class="sau-link-button sm d-block py-2">Select Payment Method</a>
                        <a v-else href="#" @click.prevent="changePaymentView(true)" class="sau-link-button sm d-block py-2 border-primary text-white bg-primary font-bold hover:text-white">{{ paymentMethodSelected ? 'Change Payment Method' : 'Select Payment Method' }}</a>
                    </div>
                </div>
                <!-- Payment Selection -->
                <div v-if="invoice.status !== 'Paid'">
                    <div v-if="paymentMethodSelected && invoice.status !== 'Payment Pending'">
                        <div class="sep"></div>
                        <div class="text-center py-2">
                            <h5>Payment Method</h5>
                            <div>
                                <transition name="fade" mode="out-in" appear>
                                    <div :key="selectedCreditCardDisplay" style="animation-duration: 0.3s">
                                        <img :src="selectedCreditCardDisplay" alt="Invoice" height="35"/>
                                        <div v-if="this.paymentMethodSelected == 'bpay'">
                                            <a href="#" id="bpay-popover" class="sau-font xs">View BPAY Biller Code</a>
                                            <b-popover target="bpay-popover"
                                                    placement="top"
                                                    triggers="hover focus">
                                                <sau-utility-bpay></sau-utility-bpay>
                                            </b-popover>
                                        </div>
                                        <div v-else-if="this.paymentMethodSelected === 'creditcard'">
                                            <small>xxxx-xxxx-xxxx-{{ this.userDetails.billing.card_last_four  }}</small>
                                        </div>
                                        <div v-else-if="this.paymentMethodSelected === 'credit'">
                                            <small>Balance: ${{ selectedPaymentMethodDetailsDisplay }}</small>
                                        </div>

                                        <div v-else>
                                            <small>{{ selectedPaymentMethodDetailsDisplay }}</small>
                                        </div>

                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>

                    <div class="sep"></div>

                    <!-- Totals -->
                    <div class="py-2">
                        <div class="d-flex sau-font sm" v-if="invoice.subtotal">
                            <div class="mr-auto">Sub Total</div>
                            <div>{{ invoice.subtotal | currency }}</div>
                        </div>
                        <div class="d-flex sau-font sm" v-if="invoice.tax">
                            <div class="mr-auto">+10% GST</div>
                            <div>{{ invoice.tax | currency }}</div>
                        </div>
                        <div class="d-flex sau-font sm" v-if="Math.round(invoice.credit)">
                            <div class="mr-auto">Credit</div>
                            <div>-{{ invoice.credit | currency }}</div>
                        </div>
                        <div class="d-flex sau-font lg" v-if="invoice.total">
                            <div class="mr-auto"><span class="sau-font-bold">Total</span></div>
                            <div><span class="sau-font-bold">{{ invoice.total | currency }}</span></div>
                        </div>
                    </div>

                    <button 
                        v-if="invoice.status != 'Paid' && invoice.status != 'Cancelled' && invoice.status != 'Payment Pending'" 
                        :disabled="!paymentAllowed" 
                        class="btn btn-block btn-success btn-lg" 
                        :class="readyToSubmit" 
                        v-b-tooltip.hover
                        :title="paymentMethodSelected ? paymentButtonLabel : 'You must select a payment method'"
                        @click.prevent="processPayment()">{{ paymentButtonLabel }} <i v-if="processing" class="far fa-spinner-third fa-spin"></i>
                    </button>

                    <template v-if="invoice.status == 'Payment Pending'">
                        <div>
                            <p class="sau-font-bold"><i class="fas fa-check-circle mr-1 text-success"></i>
                                <small>There is a payment pending for this invoice. Please allow up to 3 business days for payment to be received.</small>
                            </p>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <b-modal ref="bpayModal" title="Pay with BPAY" @ok="handleBpayPayment" ok-title="Continue with BPAY" ok-variant="success" cancel-variant="light" centered>
        <p>BPAY payments can take up to 3 business days to process. If you need to pay for your transaction urgently, please choose the Credit Card or PayPal options.</p>
        <p>Use the biller code and reference number to make a BPAY payment through your bank or financial institution.</p>
        <div class="row">
            <div class="col-lg-8 offset-lg-2">
                <sau-utility-bpay></sau-utility-bpay>
            </div>
        </div>

    </b-modal>

</div>
</template>

<script>
export default {
    data() {
        return {
            processing: false,
            paymentLabel: 'Pay Now',
            ready: false,
            processingMessage: 'Processing your payment',
        }
    },
    mounted() {

        this.getPaymentMethods().then(response => {

            this.ready = true;
        })
    },
    methods:{
        cleanItemName(item) {
            return item.type;
        },
        changePaymentView(viewType){
            this.setScroll;
            this.$store.commit('setPaymentView', viewType);
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
            });
        },
        handleBpayPayment() {
            window.location = '/billing/invoices/' + this.invoice.id + '/bpay/';
        },
        processPayment() {
            // Overwrite default functionality of the pay now button when BPay is selected
            if(this.paymentMethodSelected === 'bpay') {
                this.$refs.bpayModal.show();
                return;
            }

            if(this.paymentMethodSelected === 'credit') {
                this.processingMessage = 'Applying credit';
            }

            this.processing = true;

            let data = {
                paymentMethod: this.paymentMethodSelected
            };

            this.$swal({
                title: this.processingMessage,
                text: "Please wait...",
                imageUrl: "/img/spinner-dark-gray-80.gif",
                showConfirmButton: false,
                allowOutsideClick: false
            });

            this.payInvoice(data, this.invoice.id)
                .then(response => {
                    if(response.data.result == 'success') {
                        // Redirect to a success screen instead of invoices

                        if(response.data.invoicestatus === 'Unpaid') {
                            this.reloadInvoice();

                            this.$swal({
                                toast: true,
                                type:'success',
                                title: 'Credit Successfully Applied',
                                position: 'center',
                                showConfirmButton: false,
                                timer: 3000,
                                onOpen: function() {
                                    this.$swal.closeModal();
                                }
                            });

                        } else {

                            setTimeout(function() {
                                window.location = '/billing/invoices/' + this.invoice.id + '/complete/';
                            }.bind(this), 3000);

                        }

                    } else {

                        window.location = `/billing/invoices/${this.invoice.id}/unsuccessful`;

                    }
                    this.processing = false;
                    this.processingMessage = 'Processing your payment';
                })
                .catch(error => {
                    this.processing = false;
                    this.processingMessage = 'Processing your payment';
                    this.$swal({
                        type:'error',
                        html: '<p>There was an error while trying to pay your invoice. No payment has been taken.</p><p>Please try again, and if the problem persists contact our support team for assistance.</p>',
                        showConfirmButton: true,
                        confirmButtonClass: 'btn btn-danger',
                        buttonsStyling: false,
                    });
                });
        },
        showBpayModal () {
            this.$refs.bpayModal.show();
        },
        hideModal () {
            this.$refs.bpayModal.hide()
        },
        reloadInvoice() {

            this.getInvoice(this.invoice.id).then(response => {
                this.$store.commit('setInvoice', response.data);

            }).catch(error => {
                this.$swal({
                    type:'error',
                    title: 'There was an error while trying to update your invoice',
                    html: '<p>Credit has been successfully applied to your invoice, but something went wrong while updating your invoice. Please reload the page and try again. If the problem persists please contact support.</p>',
                    showConfirmButton: true,
                    confirmButtonClass: 'btn btn-danger',
                    buttonsStyling: false,
                });
            });

            this.getCreditBalance().then(response => {
                this.$store.commit('setUserCredit', response.data);

            }).catch(error => {
                this.$swal({
                    type:'error',
                    title: 'There was an error while trying to update your account credit',
                    html: '<p>Credit has been successfully applied to your invoice, but something went wrong while updating your balance. Please reload the page and try again. If the problem persists please contact support.</p>',
                    showConfirmButton: true,
                    confirmButtonClass: 'btn btn-danger',
                    buttonsStyling: false,
                });

            });
        }
    },
    computed: {

        readyToSubmit() {
            return (this.paymentMethodSelected === 'credit' && !Math.ceil(this.userDetails.billing.credit) ? 'btn-light' : 'btn-success');
        },
        getStatusImage() {
            let imagePath = "/img/illustrations/invoice-";
            switch(this.invoice.status) {
                case 'Payment Pending':
                    return imagePath+'blank.gif';
                        break;
                case this.invoice.status :
                    return (imagePath+this.invoice.status+'.gif').toLowerCase();
                        break;
                default :
                    return imagePath+'blank.gif';
                        break;
            }
        },
        selectedCreditCardDisplay(){
            if(!this.ready) return

            switch(this.paymentMethodSelected){
                case "creditcard":
                    return this.getCreditCardImage()
                    break;
                case "paypal":
                    return '/img/logo-paypal.gif'
                    break;
                case "bpay":
                    return '/img/bpay-landscape.gif'
                    break;
                case "credit":
                    return '/img/account-credit.gif'
                    break;
                default: '';
            }
        },
        selectedPaymentMethodDetailsDisplay() {

            switch(this.paymentMethodSelected) {
                case "creditcard":
                    return this.ccLastFour

                case "paypal":
                    return this.clientActivePaypalBAEmail

                case "bpay":
                    return this.userDetails.billing.bpay_ref ? this.userDetails.billing.bpay_ref : 'N/A'

                case "credit":
                    return this.userDetails.billing.credit

                default:
                    break;
            }
        },
        paymentButtonLabel() {
            return this.paymentMethodSelected === 'credit' ? 'Apply Credit' : 'Pay Now';
        },
        paymentAllowed() {
            if (this.paymentMethodSelected==='') {
                return false;
            } else if (this.paymentMethodSelected==='credit') {
                if (this.userDetails.billing.credit>0) {
                    return true;
                }
                return false;
            }
            return true;
        },
    },


}
</script>
<style>
</style>
